/**
 * SSOT for BE-provided order product display fields (display_kind, detail_path, order_display).
 * Internal fallbacks and category matching use ASCII/English literals only.
 */

import { resolveOrganizedTourDisplaySummary } from "../resolveOrganizedTourDisplaySummary";
import { resolveOrganizedTourCardImage } from "../resolveOrganizedTourCardImage";

const FLIGHT_ORDER_FALLBACK_TITLE = "Flight order";
const ORGANIZED_TOUR_FALLBACK_TITLE_HE = "טיול מאורגן";
const CRUISE_PRODUCT_TITLE_HE = "מנו ספנות";
const DASHBOARD_CATEGORY_FLIGHTS = "flights";
const DASHBOARD_CATEGORY_PACKAGES = "packages";
const DASHBOARD_CATEGORY_HOTELS = "hotels";
const DASHBOARD_CATEGORY_DYNAMIC_PACKAGES = "dynamic-packages";
const DASHBOARD_CATEGORY_ORGANIZED_TOURS = "organized-tours";

const BANNED_HOTEL_DISPLAY_NAMES = new Set([
  "lds package",
  "package",
  "flightshotel",
  "vacation package",
]);

function trimScalar(value) {
  if (value == null) return "";
  return String(value).trim();
}

function coerceRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value;
}

function normalizeDealtype(value) {
  return trimScalar(value).toUpperCase().replace(/[\s_-]+/g, "");
}

/** True for LDS OrganizedTour rows — must win over hotel-in-reservation heuristics. */
export function isOrganizedTourOrder(order) {
  const row = coerceRecord(order);
  if (!row) return false;

  const dealtype = normalizeDealtype(row.dealtype ?? row.deal_type);
  if (dealtype === "ORGANIZEDTOUR") return true;

  const explicitKind = trimScalar(
    row.display_kind ?? row.displayKind ?? row.product_type ?? row.product_family
  ).toLowerCase();
  if (
    explicitKind === "organized_tour" ||
    explicitKind === "organizedtour" ||
    explicitKind === "tour"
  ) {
    return true;
  }

  const category = normalizeDashboardCategorySlug(
    row.order_display_category ?? row.orderDisplayCategory ?? row.category
  );
  if (category === DASHBOARD_CATEGORY_ORGANIZED_TOURS) return true;

  const orderDisplay = coerceRecord(row.order_display ?? row.orderDisplay);
  if (orderDisplay?.kind === "organized_tour") return true;

  return false;
}

function normalizeDashboardCategorySlug(value) {
  const slug = trimScalar(value).toLowerCase();
  if (!slug) return "";
  if (slug === DASHBOARD_CATEGORY_FLIGHTS || slug === "flight") {
    return DASHBOARD_CATEGORY_FLIGHTS;
  }
  if (slug === DASHBOARD_CATEGORY_PACKAGES || slug === "package") {
    return DASHBOARD_CATEGORY_PACKAGES;
  }
  if (slug === DASHBOARD_CATEGORY_HOTELS || slug === "hotel") {
    return DASHBOARD_CATEGORY_HOTELS;
  }
  if (
    slug === DASHBOARD_CATEGORY_DYNAMIC_PACKAGES ||
    slug === "dynamic" ||
    slug === "dynamic_package" ||
    slug === "dynamic_packages"
  ) {
    return DASHBOARD_CATEGORY_DYNAMIC_PACKAGES;
  }
  if (
    slug === DASHBOARD_CATEGORY_ORGANIZED_TOURS ||
    slug === "organized_tours" ||
    slug === "organizedtours" ||
    slug === "organized_tour"
  ) {
    return DASHBOARD_CATEGORY_ORGANIZED_TOURS;
  }
  return slug;
}

function isCruiseOrder(row) {
  const feedType = trimScalar(row?.type).toLowerCase();
  if (feedType === "cruise") return true;
  const displayKind = trimScalar(row?.display_kind ?? row?.displayKind).toLowerCase();
  if (displayKind === "cruise") return true;
  const listIcon = trimScalar(row?.listIconType ?? row?.list_icon_type).toLowerCase();
  return listIcon === "cruise";
}

function isGenericFeedListTitle(value) {
  const t = trimScalar(value).toLowerCase();
  if (!t || t === "—" || t === "-") return true;
  if (BANNED_HOTEL_DISPLAY_NAMES.has(t)) return true;
  return (
    t === "חבילת נופש" ||
    t === "lds package" ||
    t === "organized tour" ||
    t === "organizedtour" ||
    t === "טיול מאורגן"
  );
}

function resolveFeedListTitle(row) {
  return trimScalar(row?.title);
}

function isResolvableHotelName(value) {
  const name = trimScalar(value);
  if (!name || name.length < 2) return false;
  if (BANNED_HOTEL_DISPLAY_NAMES.has(name.toLowerCase())) return false;
  return true;
}

function positiveScalarId(value) {
  if (value == null || value === "" || value === 0 || value === "0") return false;
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return true;
  return Boolean(trimScalar(value));
}

/** Package/hotel structure — wins over flight dealtype or linkedflights alone. */
function ldsDealTokenIndicatesHotelPackage(token) {
  const raw = trimScalar(token);
  if (!raw) return false;
  try {
    const padded = raw + "=".repeat((4 - (raw.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded));
    const name = trimScalar(
      decoded?.hotelName ?? decoded?.hotel_name ?? decoded?.name
    );
    return isResolvableHotelName(name);
  } catch {
    return false;
  }
}

export function hasLdsVacationPackageHotelComponent(order) {
  const row = coerceRecord(order);
  if (!row) return false;
  if (isOrganizedTourOrder(row)) return false;

  const dealtype = normalizeDealtype(row.dealtype ?? row.deal_type);
  if (dealtype === "FLIGHTSHOTEL") return true;

  const resolvedHotel = trimScalar(row.resolved_hotel_name ?? row.resolvedHotelName);
  if (isResolvableHotelName(resolvedHotel)) return true;
  for (const key of ["hotelName", "hotel_name", "hotelname", "HotelName"]) {
    if (isResolvableHotelName(row[key])) return true;
  }
  for (const key of ["hotelid", "hotelId", "hotel_id"]) {
    if (positiveScalarId(row[key])) return true;
  }

  const passengers = row.passengersinfo ?? row.passengers_info;
  if (Array.isArray(passengers)) {
    for (const room of passengers) {
      const roomBlock = room?.room;
      if (!roomBlock || typeof roomBlock !== "object") continue;
      for (const fld of ["ldsDealId", "lds_deal_id"]) {
        if (ldsDealTokenIndicatesHotelPackage(roomBlock[fld])) return true;
      }
    }
  }

  const linked = row.linkedflights ?? row.linkedFlights ?? row.linked_flights;
  if (linked && typeof linked === "object" && !Array.isArray(linked)) {
    if (ldsDealTokenIndicatesHotelPackage(linked.ldsDealId ?? linked.lds_deal_id)) {
      return true;
    }
  }

  const reservation = row.order_response?.reservation ?? row.orderResponse?.reservation;
  if (reservation && typeof reservation === "object") {
    const hotel = reservation.hotel;
    if (hotel && typeof hotel === "object") {
      const hotelName = trimScalar(
        hotel.hotelName ?? hotel.hotel_name ?? hotel.name ?? hotel.HotelName
      );
      if (isResolvableHotelName(hotelName)) return true;
    }
    if (Array.isArray(reservation.hotels) && reservation.hotels.length > 0) {
      return true;
    }
    for (const key of ["hotelId", "hotelid"]) {
      if (positiveScalarId(reservation[key])) return true;
    }
  }

  return false;
}

export function isFlightsOnlyOrder(order) {
  const row = coerceRecord(order);
  if (!row) return false;
  if (isOrganizedTourOrder(row)) return false;

  const dealtype = normalizeDealtype(row.dealtype ?? row.deal_type);
  if (dealtype === "FLIGHTSHOTEL") return false;

  const ext = row.extended ?? row.Extended;
  const hrModule =
    ext && typeof ext === "object" && !Array.isArray(ext)
      ? ext.hrFlightModule ?? ext.hr_flight_module
      : null;
  if (
    hrModule &&
    typeof hrModule === "object" &&
    String(hrModule.source_module || "").toLowerCase() === "flights_new" &&
    (hrModule.flight_only === true ||
      hrModule.flight_only === 1 ||
      String(hrModule.flight_only).toLowerCase() === "true")
  ) {
    return true;
  }

  if (hasLdsVacationPackageHotelComponent(row)) return false;

  const orderDisplay = coerceRecord(row.order_display ?? row.orderDisplay);
  if (orderDisplay?.kind === "package" || orderDisplay?.showHotelFields === true) {
    return false;
  }

  const feedType = trimScalar(row.type).toLowerCase();
  if (feedType === "flight" && !hasLdsVacationPackageHotelComponent(row)) return true;

  const displayKind = trimScalar(row.display_kind ?? row.displayKind).toLowerCase();
  if (displayKind === "flight" && !hasLdsVacationPackageHotelComponent(row)) return true;

  if (orderDisplay?.kind === "flight") return true;

  return dealtype === "FLIGHTS" || dealtype === "FLIGHT";
}

/** SSOT slug for dashboard tab routing. */
export function resolveCanonicalOrderProductType(order) {
  const row = coerceRecord(order);
  if (!row) return "package";
  if (isOrganizedTourOrder(row)) return "organized_tour";
  if (hasLdsVacationPackageHotelComponent(row)) return "package";
  if (isFlightsOnlyOrder(row)) return "flight";
  const dealtype = normalizeDealtype(row.dealtype ?? row.deal_type);
  if (dealtype === "ORGANIZEDTOUR") return "organized_tour";
  if (dealtype === "HOTEL" || dealtype === "HOTELOFFER" || row.hotelorderid != null) return "hotel";
  return resolveOrderDisplayKind(row);
}

/** Recent orders list row icon: hotel | flight | cruise | package | organized_tour */
export function resolveRecentOrderListIconKind(order) {
  const row = coerceRecord(order);
  if (!row) return "package";
  const explicit = trimScalar(row.listIconType ?? row.list_icon_type).toLowerCase();
  if (["hotel", "flight", "cruise", "package", "organized_tour"].includes(explicit)) {
    return explicit;
  }
  const feedType = trimScalar(row.type).toLowerCase();
  if (feedType === "hotel") return "hotel";
  if (feedType === "cruise") return "cruise";
  if (feedType === "flight") return "flight";
  if (isOrganizedTourOrder(row)) return "organized_tour";
  if (isFlightsOnlyOrder(row)) return "flight";
  if (resolveOrderDashboardCategory(row) === DASHBOARD_CATEGORY_FLIGHTS) {
    return "flight";
  }
  if (resolveOrderDashboardCategory(row) === DASHBOARD_CATEGORY_ORGANIZED_TOURS) {
    return "organized_tour";
  }
  const orderDisplay = coerceRecord(row.order_display ?? row.orderDisplay);
  const title = trimScalar(
    row.title ?? row.display_title ?? row.displayTitle ?? orderDisplay?.title
  );
  if (/[A-Z]{3}\s*→\s*[A-Z]{3}/.test(title)) return "flight";
  return "package";
}

export function resolveOrderDisplayKind(order) {
  const row = coerceRecord(order);
  if (!row) return "fallback";
  if (isOrganizedTourOrder(row)) return "organized_tour";
  if (hasLdsVacationPackageHotelComponent(row)) return "package";
  const explicit = trimScalar(row.display_kind ?? row.displayKind).toLowerCase();
  if (explicit) return explicit;
  if (isFlightsOnlyOrder(row)) return "flight";
  const dealtype = normalizeDealtype(row.dealtype ?? row.deal_type);
  if (dealtype === "FLIGHTSHOTEL") return "package";
  if (dealtype === "ORGANIZEDTOUR") return "organized_tour";
  return "package";
}

export function resolveOrderDashboardCategory(order) {
  const row = coerceRecord(order);
  if (!row) return DASHBOARD_CATEGORY_PACKAGES;
  if (isOrganizedTourOrder(row)) return DASHBOARD_CATEGORY_ORGANIZED_TOURS;
  if (hasLdsVacationPackageHotelComponent(row)) return DASHBOARD_CATEGORY_PACKAGES;
  const explicit = normalizeDashboardCategorySlug(
    row.order_display_category ?? row.orderDisplayCategory ?? row.category
  );
  if (explicit === DASHBOARD_CATEGORY_FLIGHTS) return DASHBOARD_CATEGORY_FLIGHTS;
  if (explicit === DASHBOARD_CATEGORY_PACKAGES) return DASHBOARD_CATEGORY_PACKAGES;
  if (explicit === DASHBOARD_CATEGORY_HOTELS) return DASHBOARD_CATEGORY_HOTELS;
  if (explicit === DASHBOARD_CATEGORY_DYNAMIC_PACKAGES) {
    return DASHBOARD_CATEGORY_DYNAMIC_PACKAGES;
  }
  if (explicit === DASHBOARD_CATEGORY_ORGANIZED_TOURS) {
    return DASHBOARD_CATEGORY_ORGANIZED_TOURS;
  }

  const feedType = trimScalar(row.type).toLowerCase();
  if (feedType === "hotel") return DASHBOARD_CATEGORY_HOTELS;
  if (feedType === "package") return DASHBOARD_CATEGORY_PACKAGES;
  if (feedType === "flight") return DASHBOARD_CATEGORY_FLIGHTS;
  if (
    feedType === "dynamic" ||
    feedType === "dynamic_package" ||
    feedType === "dynamic-packages"
  ) {
    return DASHBOARD_CATEGORY_DYNAMIC_PACKAGES;
  }

  const displayKind = resolveOrderDisplayKind(row);
  if (displayKind === "flight") return DASHBOARD_CATEGORY_FLIGHTS;
  if (displayKind === "hotel") return DASHBOARD_CATEGORY_HOTELS;
  if (displayKind === "dynamic_hotel_flight") return DASHBOARD_CATEGORY_DYNAMIC_PACKAGES;
  return DASHBOARD_CATEGORY_PACKAGES;
}

export function resolveOrderDetailPath(order) {
  const row = coerceRecord(order);
  const fromApi = trimScalar(row?.detail_path ?? row?.detailPath);
  if (fromApi) return fromApi;
  const orderId = trimScalar(row?.id ?? row?.orderId ?? row?.order_id);
  const category = resolveOrderDashboardCategory(row);
  if (category === DASHBOARD_CATEGORY_FLIGHTS) {
    return `/p/dashboard/orders/flights/${orderId}`;
  }
  if (category === DASHBOARD_CATEGORY_ORGANIZED_TOURS) {
    return `/p/dashboard/orders/organized-tours/${orderId}`;
  }
  if (category === DASHBOARD_CATEGORY_HOTELS) {
    return `/p/dashboard/orders/hotels/${orderId}`;
  }
  if (category === DASHBOARD_CATEGORY_DYNAMIC_PACKAGES) {
    return `/p/dashboard/orders/dynamic-packages/${orderId}`;
  }
  return `/p/dashboard/orders/packages/${orderId}`;
}

export function shouldRenderHotelFields(order) {
  const row = coerceRecord(order);
  if (isOrganizedTourOrder(row)) return false;
  const orderDisplay = coerceRecord(row?.order_display ?? row?.orderDisplay);
  if (orderDisplay && orderDisplay.showHotelFields === false) return false;
  return !isFlightsOnlyOrder(row);
}

function resolveOrganizedTourImageUrl(order) {
  const row = coerceRecord(order);
  if (!row) return null;
  for (const key of [
    "thumbnailUrl",
    "thumbnail_url",
    "heroImageUrl",
    "hero_image_url",
  ]) {
    const direct = trimScalar(row[key]);
    if (direct && (direct.startsWith("http") || direct.startsWith("//"))) {
      return direct;
    }
  }
  const op = row.ourprice ?? row.ourPrice;
  if (op && typeof op === "object") {
    const snap = trimScalar(op.organized_tour_image_url ?? op.organizedTourImageUrl);
    if (snap) return snap;
  }
  try {
    const { resolvedImage } = resolveOrganizedTourCardImage(row);
    if (resolvedImage) return resolvedImage;
  } catch {
    /* ignore */
  }
  return null;
}

function buildOrganizedTourSubtitle(order, summary) {
  const row = coerceRecord(order);
  if (!row) return null;
  const parts = [];
  const route = trimScalar(summary?.routeLabel);
  if (route) parts.push(route);
  const dep = trimScalar(
    row.dep_date ??
      row.depDate ??
      row.checkIn ??
      row.check_in ??
      row.order_display?.departureDate
  );
  const ret = trimScalar(
    row.ret_date ??
      row.retDate ??
      row.checkOut ??
      row.check_out ??
      row.order_display?.returnDate
  );
  if (dep || ret) {
    parts.push([dep || "—", ret || "—"].join(" – "));
  }
  return parts.length ? parts.join(" · ") : null;
}

/** True when subtitle already embeds the same dep/ret tokens shown in labeled date rows. */
function organizedTourSubtitleDuplicatesLabeledDates(subtitle, departureDate, returnDate) {
  const sub = trimScalar(subtitle);
  if (!sub) return false;
  const dep = trimScalar(departureDate);
  const ret = trimScalar(returnDate);
  if (!dep && !ret) return false;
  return (!dep || sub.includes(dep)) && (!ret || sub.includes(ret));
}

/** Recent-order cards: hide יציאה/חזרה rows when organized tour subtitle already shows the range. */
export function shouldShowRecentOrderLabeledDates(product) {
  if (!product?.isOrganizedTourPresentation) return true;
  return product.showLabeledDateRows !== false;
}

export function resolveOrderTitleForDisplay(order) {
  const row = coerceRecord(order);
  if (!row) return "";
  if (isCruiseOrder(row)) return CRUISE_PRODUCT_TITLE_HE;
  const orderDisplay = coerceRecord(row.order_display ?? row.orderDisplay);
  const displayTitle = trimScalar(
    orderDisplay?.title ?? row.display_title ?? row.displayTitle
  );
  if (displayTitle && !isOrganizedTourOrder(row)) return displayTitle;

  if (isOrganizedTourOrder(row)) {
    const summary = resolveOrganizedTourDisplaySummary(row, { silent: true });
    const feedTitle = resolveFeedListTitle(row);
    if (feedTitle && !isGenericFeedListTitle(feedTitle)) {
      return feedTitle;
    }
    if (summary.tourName && summary.tourNameSource !== "fallback") {
      return summary.tourName;
    }
    if (displayTitle) return displayTitle;
    return summary.tourName || ORGANIZED_TOUR_FALLBACK_TITLE_HE;
  }

  if (displayTitle) return displayTitle;

  const feedTitle = resolveFeedListTitle(row);
  if (feedTitle && !isGenericFeedListTitle(feedTitle)) {
    return feedTitle;
  }

  if (isFlightsOnlyOrder(row)) {
    const passenger = trimScalar(orderDisplay?.passengerSummary);
    const route = trimScalar(
      orderDisplay?.routeLabel ?? row.destination ?? row.route ?? row.route_label
    );
    const pnr = trimScalar(orderDisplay?.pnr);
    return route || passenger || pnr || FLIGHT_ORDER_FALLBACK_TITLE;
  }
  const hotelDisplay = trimScalar(row.hotel_display_name ?? row.hotelDisplayName);
  if (isResolvableHotelName(hotelDisplay)) return hotelDisplay;
  const resolvedHotel = trimScalar(row.resolved_hotel_name ?? row.resolvedHotelName);
  if (isResolvableHotelName(resolvedHotel)) return resolvedHotel;
  const hotelName = trimScalar(row.hotelName ?? row.hotel_name);
  if (isResolvableHotelName(hotelName)) return hotelName;
  return hotelName || feedTitle || "";
}

export function resolveOrderProductDisplay(order) {
  const row = coerceRecord(order) || {};
  const orderDisplay = coerceRecord(row.order_display ?? row.orderDisplay) || {};
  const displayKind = resolveOrderDisplayKind(row);
  const organizedTour = isOrganizedTourOrder(row);
  const showHotelFields = shouldRenderHotelFields(row);
  const dealtype = normalizeDealtype(row.dealtype ?? row.deal_type);
  const isExplicitFlightFromApi =
    trimScalar(row.display_kind ?? row.displayKind).toLowerCase() === "flight" ||
    orderDisplay.kind === "flight";

  const summary = organizedTour
    ? resolveOrganizedTourDisplaySummary(row, { silent: true })
    : null;
  const title = resolveOrderTitleForDisplay(row);
  const subtitle = organizedTour ? buildOrganizedTourSubtitle(row, summary) : null;
  const imageUrl = organizedTour ? resolveOrganizedTourImageUrl(row) : null;
  const departureDate =
    trimScalar(orderDisplay.departureDate) ||
    trimScalar(row.dep_date ?? row.depDate ?? row.checkIn ?? row.check_in) ||
    null;
  const returnDate =
    trimScalar(orderDisplay.returnDate) ||
    trimScalar(row.ret_date ?? row.retDate ?? row.checkOut ?? row.check_out) ||
    null;
  const showLabeledDateRows = organizedTour
    ? !organizedTourSubtitleDuplicatesLabeledDates(subtitle, departureDate, returnDate)
    : true;

  const product = {
    displayKind,
    category: resolveOrderDashboardCategory(row),
    detailPath: resolveOrderDetailPath(row),
    showHotelFields,
    title,
    subtitle,
    imageUrl,
    imageSource: organizedTour
      ? imageUrl
        ? "organized_tour_resolved"
        : "none"
      : null,
    routeLabel: trimScalar(orderDisplay.routeLabel) || summary?.routeLabel || null,
    passengerSummary: trimScalar(orderDisplay.passengerSummary) || null,
    baggageSummary: trimScalar(orderDisplay.baggageSummary) || null,
    departureDate,
    returnDate,
    showLabeledDateRows,
    isOrganizedTourPresentation: organizedTour,
    isFlightPresentation:
      organizedTour
        ? false
        : hasLdsVacationPackageHotelComponent(row)
          ? false
          : dealtype === "FLIGHTSHOTEL"
            ? false
            : isExplicitFlightFromApi || displayKind === "flight",
  };

  if (import.meta.env?.DEV) {
    console.info("[ORDER_PRODUCT_DISPLAY_TRACE]", {
      orderId: row.id ?? row.orderId ?? null,
      dealtype: row.dealtype ?? row.deal_type ?? null,
      displayKind: product.displayKind,
      category: product.category,
      isOrganizedTour: organizedTour,
      titleCandidates: {
        feedTitle: row.title ?? null,
        displayTitle: row.display_title ?? row.displayTitle ?? orderDisplay?.title ?? null,
        tourName: summary?.tourName ?? null,
        tourNameSource: summary?.tourNameSource ?? null,
      },
      resolvedTitle: product.title,
      resolvedSubtitle: product.subtitle,
      resolvedImage: product.imageUrl,
      listIconKind: resolveRecentOrderListIconKind(row),
    });
  }

  return product;
}
